import * as React from 'react';
import type { CurrencyCode } from '@insio/shared-types';
import { cn } from '../../../utils';
import { FieldType } from '../../../types';
import type { ValidationIssue } from '../../../types';
import { EditableField } from '../editable-field/EditableField';
import { resolveEditorConfig } from './utils/resolve-editor-config';
import { EmptyFormatter } from './formatters/EmptyFormatter';
import { FormatterError } from './formatters/FormatterError';
import { isBlank } from '@insio/string-utils';

export type RenderFnParams = {
  value: unknown;
  onChange: (val: unknown) => void;
  /**
   * Commit the value immediately. The returned promise rejects if `onSave`
   * rejects, so modal-based editors can keep themselves open and surface the
   * failure rather than closing on a failed save.
   */
  onCommit: (val: unknown) => Promise<void>;
  onCancel: () => void;
  onBlur: () => void;
  /** Validation issues forwarded from `FieldEditor.errors`. */
  errors?: ValidationIssue[];
};

export type RenderFn = (params: RenderFnParams) => React.ReactNode;

export interface FieldEditorProps {
  id?: string;
  fieldType: FieldType;
  labelSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** External (final) value – controlled prop */
  value: unknown;

  /** Callback after saving new value */
  onSave: (value: unknown) => void | Promise<void>;

  /**
   * Optional currency code for editors that need it (e.g. decimal).
   * Passed to editor config resolvers and threaded into editors/formatters
   * as needed.
   */
  currencyCode?: CurrencyCode;

  /**
   * Render prop for custom edit input.
   * Returns e.g. <input>, <textarea> or custom component.
   */
  render?: RenderFn;

  /**
   * Optional: custom renderer for display mode (editing = false).
   * Default is <span>{value}</span>. Receives the field's issues as its second argument.
   */
  formatter?: (value: unknown, errors?: ValidationIssue[]) => React.ReactNode;

  /**
   * Optional: placeholder text to display when value is empty/null/undefined.
   * Rendered with foreground-muted styling.
   * @default '—'
   */
  placeholder?: string;

  className?: string;

  /**
   * Optional: disabled mode – prevents editing
   */
  disabled?: boolean;

  /**
   * Optional: display mode – 'inline' (default) or 'block'
   * @default 'inline'
   */
  display?: 'inline' | 'block';

  /**
   * Accessible label for the confirm/save icon button.
   * Pass a translated string; falls back to 'Save'.
   * @default 'Save'
   */
  saveLabel?: string;

  /**
   * Accessible label for the cancel icon button.
   * Pass a translated string; falls back to 'Cancel'.
   * @default 'Cancel'
   */
  cancelLabel?: string;

  /**
   * Optional: validation issues to surface on the field. Because a single
   * FieldEditor (e.g. `bankingDetails`) can map to multiple underlying fields,
   * the prop accepts an array. The worst severity wins; messages are joined
   * with ' · ' into the tooltip.
   */
  errors?: ValidationIssue[];

  /**
   * When true, focusing the field via keyboard (Tab) auto-activates edit
   * mode. Use only for lightweight inline editors — avoid for composite/modal
   * editors (invoice lines, VAT summary) where it would be disruptive.
   *
   * @default false
   */
  openOnFocus?: boolean;

  /**
   * Optional controlled editing flag. When set, the parent owns edit-mode
   * state. Useful when wrapping the editor in a focusable row that should
   * drive open/close from its own focus management.
   */
  editing?: boolean;

  /**
   * Fires whenever the editor wants to transition between display and edit
   * mode (e.g. internal Save, Cancel, Escape, click-outside, openOnFocus).
   */
  onEditingChange?: (editing: boolean) => void;

  bordered?: boolean;
  /** Apply input-like styling in display mode. @see EditableField.alwaysActive */
  alwaysActive?: boolean;
}

/**
 * Implementation of EditableField,
 * resolves the editor by fieldType and delegates to the generic
 * EditableField component for the rest of the logic.
 */
export function FieldEditor({
  id,
  className,
  fieldType,
  currencyCode,
  value: externalValue,
  onSave,
  render,
  formatter,
  placeholder = '—',
  disabled = false,
  display = 'inline',
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  errors,
  openOnFocus = false,
  editing,
  onEditingChange,
  bordered = true,
  alwaysActive = false
}: FieldEditorProps) {
  const {
    editor: Editor,
    formatter: ConfigFormatter,
    hideActions,
    display: configDisplay,
    valueGuard,
    handleErrorDisplay
  } = resolveEditorConfig(fieldType, { currencyCode });
  const guardViolation = valueGuard ? valueGuard(externalValue) : null;
  const editorRender =
    render ??
    ((params: RenderFnParams): React.ReactNode => (
      <Editor
        id={id ?? 'field-editor'}
        value={params.value}
        onChange={params.onChange}
        onCommit={params.onCommit}
        onCancel={params.onCancel}
        onBlur={params.onBlur}
        errors={params.errors}
      />
    ));
  const editorFormatter = guardViolation
    ? () => (
        <span className='text-error-text font-mono text-xs'>
          [FieldEditor] {fieldType}: {guardViolation}
        </span>
      )
    : (formatter ??
      ((val: unknown, errs?: ValidationIssue[]) => {
        if (isBlank(val)) {
          return (
            <span className='inline-flex flex-col gap-2xs items-start'>
              <EmptyFormatter blankValue={placeholder} />
              <FormatterError issues={errs} />
            </span>
          );
        }
        return <ConfigFormatter value={val} errors={errs} />;
      }));
  return (
    <EditableField
      bordered={bordered}
      alwaysActive={alwaysActive}
      value={externalValue}
      onSave={onSave}
      render={editorRender}
      formatter={guardViolation ? editorFormatter : (formatter ?? editorFormatter)}
      placeholder={placeholder}
      className={cn('editable-field', className)}
      disabled={disabled}
      display={display}
      saveLabel={saveLabel}
      cancelLabel={cancelLabel}
      errors={errors}
      hideActions={hideActions}
      rounded={configDisplay === 'custom' ? '2xl' : 'md'}
      showErrors={!handleErrorDisplay} // Text fields often have multi-line formatters that don't work well with the tooltip, so we disable it for text fields by default. It can still be enabled by passing `showErrors` explicitly.
      openOnFocus={openOnFocus}
      editing={editing}
      onEditingChange={onEditingChange}
    />
  );
}

FieldEditor.displayName = 'FieldEditor';
